import { computed, DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { GradingTemplateCategoryType } from '@lb/api/core/interfaces';
import { ILessonDetail, ILessonDetailSaveRequest, IProjectCriteriaGradeSaveRequest, ISemesterFinalLessonDetailSaveRequest } from '@lb/api/mylb/interfaces';
import { LessonDetailService } from '@lb/api/mylb/services';
import { AttendanceService } from '@lb/api/mylb/services/lesson/attendance.service';
import { LessonDetailStateService } from './lesson-detail-state.service';
import { MDAlertService } from '@melodic-ui/components';
import { AnalyticsEvent, AnalyticsService } from '@lb/services';
import { IProjectMilestoneProgress } from '@lb/api/mylb/interfaces/lesson/ilesson-detail.interface';
import { ISemesterDetail } from '@lb/api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { IProjectGradeResult } from './lesson-detail-project-final/semester-project-grade/semester-project-grade.component';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { usesLessonDetailsV2 } from '@lb/functions';
import { canCompleteLesson, getLessonEditWindowOpensAt, isLessonEditWindowOpen } from './utils/lesson-edit-window.util';
import { isLessonEditable } from '@lb/components/lessons/utils/lesson-helper.util';
import { getSavedMilestoneForLesson, resolveMilestonesToSave } from './utils/lesson-project-milestone.util';

/**
 * How often the edit-window gate re-checks the clock while it is still closed. Short because the
 * deadline is the lesson's start time, which an instructor may be sitting on the page waiting for.
 */
const EDIT_WINDOW_POLL_MS = 5_000;

export interface IAttendanceSaveParams {
	statusID: number;
	generateInvoice: boolean;
	/**
	 * Set for multi-attendance (chirp room) lessons: who was present. Routes the save through the
	 * roster endpoint — the server records Absent for every other in-window member and derives the
	 * session value itself, so statusID is ignored in this mode.
	 */
	presentEnrollmentMemberIDs?: number[];
}

export interface IHomeworkLink {
	name: string;
	url: string;
}

export interface ILessonFormValue {
	categoryGrades: Record<number, number | null>;
	selectedObjectives: number[];
	completedMilestones: number[];
	selectedProjectMilestoneID: number | null;
	projectNotes: string;
	projectCriteriaGrades: Record<number, number | null>;
	homeworkCategoryIDs: number[];
	homeworkInstructions: string;
	homeworkLinks: IHomeworkLink[];
	summary: string;
	areasOfStrength: string;
	areasOfImprovement: string;
}

@Injectable()
export class LessonDetailFormStateService {
	private readonly lessonDetailService = inject(LessonDetailService);
	private readonly attendanceService = inject(AttendanceService);
	private readonly state = inject(LessonDetailStateService);
	private readonly alertService = inject(MDAlertService);
	private readonly analytics = inject(AnalyticsService);
	private readonly appSettings = inject<IAppSettings>(APP_CONFIG);
	private readonly destroyRef = inject(DestroyRef);

	/**
	 * Polled clock backing the edit-window gate. The window opens on a wall-clock deadline rather
	 * than on anything the user does, so an instructor who opens the page just before the lesson
	 * starts sees the form unlock in place instead of having to reload.
	 */
	private readonly _now = signal(new Date());

	constructor() {
		const handle = setInterval(() => {
			// Nothing re-closes the window once it is open, so stop waking change detection.
			if (!this.isEditWindowOpen()) {
				this._now.set(new Date());
			}
		}, EDIT_WINDOW_POLL_MS);

		this.destroyRef.onDestroy(() => clearInterval(handle));

		// Hydration lives here, beside the signals it fills, so providing this service and hydrating it
		// are one act: every host that lists it in `providers` gets a populated form. It used to be two
		// effects in EnrollmentLessonWrapperComponent alone, which left the lesson-context page
		// (LessonDetailComponent) rendering completed lessons as an empty, unlocked form.

		// Lesson-owned data — per-lesson grades, objectives, project note, homework, feedback — whenever
		// the lesson loads or reloads after a save.
		effect(() => {
			const lesson = this.state.lesson();
			if (lesson?.grades) {
				untracked(() => this.hydrateFromLesson(lesson));
			}
		});

		// Enrollment-owned data — semester project grade, criteria grades, project progress, the
		// finalized-semester lock — whenever the semester detail changes. Runs independently so a slow
		// enrollment detail response neither blocks nor overwrites the lesson data above.
		effect(() => {
			const semester = this.state.currentSemester();
			// Read the project category so this re-runs once the grading template has loaded: the project
			// final grade is looked up by that category's ID.
			this.state.projectCategory()?.gradingTemplateCategoryID;
			untracked(() => this.hydrateFromSemester(semester));
		});
	}

	/**
	 * Lesson feedback (areas of strength/improvement) is only collected — and therefore only
	 * required — when the lesson uses the v2 lesson-details experience (flag on + enrollment
	 * grading config stamped). For v1 lessons the detail page isn't reachable and attendance is
	 * taken via the legacy flow, so these fields must not gate saving. Keeps the requirement
	 * scoped to "when the fields are actually shown in the UI".
	 */
	private readonly lessonDetailsEnabled = computed(() => usesLessonDetailsV2(this.state.lesson(), this.appSettings));

	/**
	 * Whether this lesson collects areas of strength / areas of improvement. Chirp room lessons don't:
	 * a room is taught to a group, so there is no single learner to write per-lesson feedback about —
	 * they keep the (optional) lesson notes only. Every other lesson type (instructional, mastery,
	 * tutoring) collects all three, with strength/improvement required as usual.
	 *
	 * Single source of truth for both the summary UI and the validation below, so a hidden field can
	 * never block the save button.
	 */
	readonly collectsLessonFeedback = computed(() => this.lessonDetailsEnabled() && !this.state.isChirpRoom());

	isFinalSemesterGradeSubmitted = signal(false);

	// Editing is locked once a semester's final grades are submitted.
	// NOTE: to let admins keep editing a finalized semester, append ` && !this.state.isAdmin()` here.
	isLocked = computed(() => this.isFinalSemesterGradeSubmitted());

	/**
	 * False when the backend says the viewer may not change this lesson — the enrollment's current
	 * instructor viewing a lesson a previous instructor or a substitute taught. They can read it all to
	 * pick the enrollment up, but saves belong to the instructor whose era it was.
	 */
	readonly isEditableByViewer = computed(() => isLessonEditable(this.state.lesson()));

	// Single source of truth for the read-only experience:
	// learners / parent contacts (experience !== 'instructor'), a finalized (locked) semester, OR a
	// lesson from another instructor's era.
	isReadOnly = computed(() => this.state.experience() !== 'instructor' || this.isLocked() || !this.isEditableByViewer());

	/**
	 * True once the lesson has started — see lesson-edit-window.util for the rule. This is the gate on
	 * *attendance and completion*, not on entering details: the detail fields open earlier so an
	 * instructor can pre-populate a lesson they plan ahead and save it as a draft.
	 */
	readonly isEditWindowOpen = computed(() => isLessonEditWindowOpen(this.state.lesson(), this._now()));

	canTakeAttendanceForLesson = computed(() => {
		if (!this.state.loaded()) {
			return false;
		}

		// Attendance opens with the lesson and not a moment earlier — recording it completes the lesson
		// and generates the invoice, so it can't be pre-filled the way the detail fields can.
		// Read-only viewers can't take it either: this also drives the chirp roster, which is not
		// otherwise isReadOnly-aware.
		return !this.isReadOnly() && this.isEditWindowOpen() && this.state.lessonRequired().attendanceStatusID == null;
	});

	/** The instant the edit window opens (the lesson's start), for the "not yet" notice. Null when it has no start date. */
	readonly editWindowOpensAt = computed(() => getLessonEditWindowOpensAt(this.state.lesson()));

	/**
	 * Whether the lesson may be *completed*: once its edit window has opened, or on an
	 * already-completed lesson, which stays saveable regardless of the clock. Completing a lesson is
	 * what records attendance and the invoice, so this keeps the original start-time gate. Read-only
	 * viewers are excluded separately via isReadOnly — this signal is only about timing.
	 */
	readonly canCompleteLesson = computed(() => canCompleteLesson(this.state.lesson(), this._now()));

	/**
	 * Whether the lesson's details (grades, objectives, project progress, homework, feedback) may be
	 * typed in. Deliberately *not* time-gated: instructors who plan ahead pre-populate a lesson
	 * before it starts and save it as a draft. Attendance is the part that still waits for the
	 * lesson to begin — see canTakeAttendanceForLesson and canCompleteLesson.
	 */
	readonly canEditDetails = computed(() => !this.isReadOnly());

	/**
	 * Bound to `[disabled]` on every instructor-facing detail control. Read-only viewers never render
	 * these controls at all, so in practice this only guards against a finalized semester.
	 *
	 * NOTE: this no longer encodes the lesson-start gate. Anything that must still wait for the
	 * lesson to start (the attendance roster) has to bind to canTakeAttendanceForLesson instead.
	 */
	readonly fieldsDisabled = computed(() => !this.canEditDetails());

	// The "can be completed" signal: gated on the lesson having started as well as having something
	// to save, so every consumer (Complete button, project-grade dialog) inherits the restriction.
	// Draft saves use canSaveDraft below, which is deliberately not gated on the clock.
	canSave = computed(() => {
		return this.canCompleteLesson() && (this.isDirty() || this.canTakeAttendanceForLesson());
	});

	/**
	 * Whether the "Save as Draft" affordance applies. It exists only in the window before the lesson
	 * may be completed — once it starts, Complete is the single save path and a draft has no reason
	 * to exist. Final-semester lessons are excluded: that flow is a semester wrap-up, not something
	 * an instructor plans ahead.
	 */
	readonly showSaveDraft = computed(() => !this.isReadOnly() && !this.canCompleteLesson() && !this.state.isFinalSemester());

	/**
	 * Whether a draft save can be performed right now. Content-only, so unlike canSave it asks
	 * nothing of validation — partial data is the entire point of a draft.
	 */
	readonly canSaveDraft = computed(() => this.showSaveDraft() && this.isDirty() && !this.state.isSaving());

	// --- Daily Grades ---
	readonly categoryGrades = signal<Record<number, number | null>>({});

	// --- Objectives ---
	readonly selectedObjectives = signal<number[]>([]);
	readonly savedObjectives = signal<Set<number>>(new Set());
	readonly newObjectives = computed(() => {
		const current = this.selectedObjectives();
		const saved = this.savedObjectives();
		return current.filter((id) => !saved.has(id));
	});

	// --- Project Progress ---
	readonly completedMilestones = signal<Set<number>>(new Set());
	readonly selectedProjectMilestoneID = signal<number | null>(null);
	readonly savedMilestoneDetails = signal<IProjectMilestoneProgress[]>([]);
	/**
	 * The milestones this save sends. Diffed against what *this lesson* stores — never against the
	 * semester's milestones, which is what silently dropped progress rows for every lesson after the
	 * first one to reach a given milestone. See lesson-project-milestone.util.
	 */
	readonly newMilestones = computed(() =>
		resolveMilestonesToSave(this.selectedProjectMilestoneID(), this.savedMilestoneDetails(), this.state.lesson()?.lessonID ?? null)
	);
	readonly projectNotes = signal<string>('');

	private syncSelectedProjectMilestoneForLesson(lessonID: number | null): void {
		this.selectedProjectMilestoneID.set(getSavedMilestoneForLesson(this.savedMilestoneDetails(), lessonID));
	}

	private resolveSemesterStoredGradeID(
		grade: { overallCalculatedGradeID: number; overridenGradeID: number; isOverriden: boolean } | null | undefined
	): number | null {
		if (!grade) {
			return null;
		}

		if (grade.isOverriden && grade.overridenGradeID > 0) {
			return grade.overridenGradeID;
		}

		return grade.overallCalculatedGradeID > 0 ? grade.overallCalculatedGradeID : null;
	}

	// --- Project Grading ---
	readonly projectCriteriaGrades = signal<Record<number, number | null>>({});
	readonly projectFinalGradeID = signal<number | null>(null);
	readonly projectFinalGrade = computed(() => {
		const gradeID = this.projectFinalGradeID();
		if (gradeID == null) return null;

		return this.state.grades().find((g) => g.gradeID === gradeID) ?? null;
	});

	// --- Independent Work (Homework) ---
	readonly selectedHomeworkCategoryIDs = signal<number[]>([]);
	readonly homeworkInstructions = signal<string>('');
	readonly homeworkLinks = signal<IHomeworkLink[]>([]);

	// --- Lesson Feedback ---
	// `summary` is the (now optional) lesson notes field; strength/improvement are required feedback.
	readonly summary = signal<string>('');
	readonly areasOfStrength = signal<string>('');
	readonly areasOfImprovement = signal<string>('');

	// --- Dirty Tracking ---
	private readonly _snapshot = signal<string>('');

	isDirty = computed(() => JSON.stringify(this.getFormValue()) !== this._snapshot());

	// --- Validation ---

	private readonly _attemptedSubmit = signal(false);

	isValid = computed(() => {
		if (this.state.isFinalSemester()) {
			return this.isFinalSemesterValid();
		}
		return this.isStandardLessonValid();
	});

	private isStandardLessonValid(): boolean {
		// Lesson feedback: areas of strength and improvement are required wherever they're collected
		// (notes stay optional). Where they aren't — feature off, or a chirp room — the fields aren't
		// shown, so they must not gate saving.
		if (this.collectsLessonFeedback() && (!this.areasOfStrength()?.trim() || !this.areasOfImprovement()?.trim())) {
			return false;
		}

		// Non-instructional lessons — and lessons on a not-graded template (chirp rooms) — are
		// feedback-only: they render no grade/project/homework controls, so the lesson feedback
		// is the only requirement.
		if (!this.state.isInstructional() || !(this.state.lesson()?.isGraded ?? true)) {
			return true;
		}

		const categoryGrades = this.categoryGrades();
		// Every category is required per lesson, except the project category which is graded per
		// semester (its per-lesson requirement is the milestone selection below).
		const requiredCategories = this.state.nonProjectCategories();

		for (const category of requiredCategories) {
			const categoryID = category.gradingTemplateCategoryID;
			const hasCategoryEntry = Object.prototype.hasOwnProperty.call(categoryGrades, categoryID);

			if (category.categoryType === GradingTemplateCategoryType.Homework) {
				// Homework must be explicitly selected, but "No Grade" (null) is valid.
				if (!hasCategoryEntry) {
					return false;
				}
				continue;
			}

			if (categoryGrades[categoryID] == null) {
				return false;
			}
		}

		// Objectives: at least one is required, but only when the section is shown
		// (catalog assigned) and there are actually objectives to pick from — a catalog
		// with zero configured objectives must not block saving.
		if (this.state.hasCourseCatalog() && this.state.objectiveOptions().length > 0 && this.selectedObjectives().length === 0) {
			return false;
		}

		// Project progress: one milestone selection is required for every lesson
		if (this.selectedProjectMilestoneID() == null) {
			return false;
		}

		// Homework: at least one category is required
		if (this.selectedHomeworkCategoryIDs().length === 0) {
			return false;
		}

		return true;
	}

	private isFinalSemesterValid(): boolean {
		const projectFinalGradeID = this.projectFinalGradeID();
		const projectCriteriaGrades = this.projectCriteriaGrades();

		if (this.isAreasOfStrengthInvalid() || this.isAreasOfImprovementInvalid()) {
			return false;
		}

		// Need both project final grade and all project criteria grades to be valid
		if (projectFinalGradeID == null) {
			return false;
		}

		for (const criteria of this.state.projectCategory()?.criteria ?? []) {
			if (projectCriteriaGrades[criteria.criteriaID] == null) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Returns true if a specific category is required but missing a grade AND user has attempted submit.
	 * Use in templates to conditionally style sections red: [type]="isCategoryInvalid(categoryID) ? 'error' : 'default'"
	 */
	isCategoryInvalid(categoryID: number): boolean {
		if (!this._attemptedSubmit()) {
			return false;
		}

		const categoryGrades = this.categoryGrades();
		const category = this.state.gradingTemplateCategories().find((c) => c.gradingTemplateCategoryID === categoryID);

		if (!category || category.categoryType === GradingTemplateCategoryType.Project) {
			return false;
		}

		if (category.categoryType === GradingTemplateCategoryType.Homework) {
			// Homework required means user must choose a value (including explicit null / No Grade).
			return !Object.prototype.hasOwnProperty.call(categoryGrades, categoryID);
		}

		return categoryGrades[categoryID] == null;
	}

	/**
	 * Returns true if areas of strength is required but missing AND user has attempted submit.
	 * Use in templates to conditionally style the feedback section red.
	 */
	isAreasOfStrengthInvalid(): boolean {
		if (!this.collectsLessonFeedback() || !this._attemptedSubmit()) {
			return false;
		}

		return !this.areasOfStrength()?.trim();
	}

	/**
	 * Returns true if areas of improvement is required but missing AND user has attempted submit.
	 * Use in templates to conditionally style the feedback section red.
	 */
	isAreasOfImprovementInvalid(): boolean {
		if (!this.collectsLessonFeedback() || !this._attemptedSubmit()) {
			return false;
		}

		return !this.areasOfImprovement()?.trim();
	}

	/**
	 * Returns true if objectives are required but none are selected AND user has attempted submit.
	 * Use in templates: [type]="isObjectivesInvalid() ? 'error' : 'default'"
	 */
	isObjectivesInvalid(): boolean {
		if (!this._attemptedSubmit()) {
			return false;
		}

		return this.state.objectiveOptions().length > 0 && this.selectedObjectives().length === 0;
	}

	/**
	 * Returns true if project progress is required but no milestone is selected AND user has attempted submit.
	 * Use in templates: [type]="isProjectProgressInvalid() ? 'error' : 'default'"
	 */
	isProjectProgressInvalid(): boolean {
		if (!this._attemptedSubmit()) {
			return false;
		}

		return this.selectedProjectMilestoneID() == null;
	}

	/**
	 * Returns true if homework is required but no category is selected AND user has attempted submit.
	 * Use in templates: [type]="isHomeworkInvalid() ? 'error' : 'default'"
	 */
	isHomeworkInvalid(): boolean {
		if (!this._attemptedSubmit()) {
			return false;
		}

		return this.selectedHomeworkCategoryIDs().length === 0;
	}

	isProjectFinalGradeInvalid(): boolean {
		if (!this._attemptedSubmit()) {
			return false;
		}
		return this.projectFinalGradeID() == null;
	}

	/**
	 * The message to show when a save is rejected. The API answers a refused save with the standard
	 * ErrorResponse contract, whose `message` is written to be read by the person who tripped it
	 * ("Select the project milestone this lesson reached before completing it."). Swallowing that in
	 * favour of "please try again" turns an actionable server-side rule into a dead end.
	 */
	private saveErrorMessage(error: unknown, fallback: string): string {
		return (error as { error?: { message?: string } })?.error?.message || fallback;
	}

	// --- Public API ---

	getFormValue(): ILessonFormValue {
		return {
			categoryGrades: this.categoryGrades(),
			selectedObjectives: this.selectedObjectives(),
			completedMilestones: [...this.completedMilestones()].sort(),
			selectedProjectMilestoneID: this.selectedProjectMilestoneID(),
			projectNotes: this.projectNotes(),
			projectCriteriaGrades: this.projectCriteriaGrades(),
			homeworkCategoryIDs: this.selectedHomeworkCategoryIDs(),
			homeworkInstructions: this.homeworkInstructions(),
			homeworkLinks: this.homeworkLinks(),
			summary: this.summary(),
			areasOfStrength: this.areasOfStrength(),
			areasOfImprovement: this.areasOfImprovement()
		};
	}

	markClean(): void {
		this._snapshot.set(JSON.stringify(this.getFormValue()));
	}

	reset(): void {
		this.categoryGrades.set({});
		this.selectedObjectives.set([]);
		this.savedObjectives.set(new Set());
		this.completedMilestones.set(new Set());
		this.selectedProjectMilestoneID.set(null);
		this.savedMilestoneDetails.set([]);
		this.projectNotes.set('');
		this.projectCriteriaGrades.set({});
		this.selectedHomeworkCategoryIDs.set([]);
		this.homeworkInstructions.set('');
		this.homeworkLinks.set([]);
		this.summary.set('');
		this.areasOfStrength.set('');
		this.areasOfImprovement.set('');
		this._attemptedSubmit.set(false);
		this._snapshot.set(JSON.stringify(this.getFormValue()));
	}

	/**
	 * Hydrate form state from the lesson API response.
	 * Only sets data owned by the lesson endpoint: per-lesson grades, objectives, and project notes.
	 * Call hydrateFromSemester() separately when enrollment detail is available.
	 */
	hydrateFromLesson(lesson: ILessonDetail): void {
		const gradeMap: Record<number, number | null> = {};
		this._attemptedSubmit.set(false);
		const locked = new Set<number>();

		for (const g of lesson.grades) {
			gradeMap[g.gradingTemplateCategoryID] = g.gradeID;
			if (g.isLocked) {
				locked.add(g.gradingTemplateCategoryID);
			}
		}

		this.categoryGrades.set(gradeMap);
		this.syncSelectedProjectMilestoneForLesson(lesson.lessonID);
		this.projectNotes.set(lesson.lessonProjectNote ?? '');
		this.summary.set(lesson.lessonSummary ?? '');
		this.areasOfStrength.set(lesson.areasOfStrength ?? '');
		this.areasOfImprovement.set(lesson.areasOfImprovement ?? '');

		// --- Homework ---
		if (lesson.homework) {
			this.selectedHomeworkCategoryIDs.set(lesson.homework.categories.map((c) => c.homeworkCategoryID));
			this.homeworkInstructions.set(lesson.homework.notes ?? '');
			this.homeworkLinks.set(lesson.homework.links.map((l) => ({ name: l.displayText, url: l.homeworkLink })));
		} else {
			this.selectedHomeworkCategoryIDs.set([]);
			this.homeworkInstructions.set('');
			this.homeworkLinks.set([]);
		}

		// --- Objectives ---
		const savedObjectiveIDs = lesson.courseObjectives ?? [];
		this.savedObjectives.set(new Set(savedObjectiveIDs));
		this.selectedObjectives.set([...savedObjectiveIDs]);

		this.markClean();
	}

	/**
	 * Hydrate form state from the enrollment detail API response.
	 * Only sets data owned by the enrollment endpoint: semester grades, project criteria grades, and project progress.
	 * Safe to call independently — merges semester overrides on top of existing lesson grades.
	 */
	hydrateFromSemester(semester: ISemesterDetail | null): void {
		// --- Semester Project Final Grade ---
		// Prefer the project category row from semesterGrades; fall back to semesterFinalGrades.
		const projectCategoryID = this.state.projectCategory()?.gradingTemplateCategoryID;
		const projectSemesterGrade =
			projectCategoryID != null ? semester?.semesterGrades?.find((g) => g.gradingTemplateCategoryID === projectCategoryID) ?? null : null;
		const projectGradeID = this.resolveSemesterStoredGradeID(projectSemesterGrade);
		this.projectFinalGradeID.set(projectGradeID);

		// --- Semester Project Criteria Grades ---
		const semesterProjectGrades = semester?.projectDetail?.semesterProjectGrades ?? [];

		if (semesterProjectGrades.length) {
			const criteriaGrades: Record<number, number | null> = {};
			for (const pg of semesterProjectGrades) {
				criteriaGrades[pg.projectCriteriaID] = pg.projectCriteriaGradeID;
			}
			this.projectCriteriaGrades.set(criteriaGrades);
		} else {
			this.projectCriteriaGrades.set({});
		}

		// --- Project Progress ---
		// completedMilestones is the semester-wide "reached" set behind the progress bar. The
		// per-lesson selection is derived from savedMilestoneDetails instead — the two are different
		// questions, and conflating them is what dropped progress rows.
		const projectProgress = semester?.projectDetail?.projectProgress ?? [];
		this.completedMilestones.set(new Set(projectProgress.map((p) => p.projectMilestoneID)));
		this.savedMilestoneDetails.set(projectProgress);
		this.syncSelectedProjectMilestoneForLesson(this.state.lesson()?.lessonID ?? null);

		this.isFinalSemesterGradeSubmitted.set(semester?.semesterFinalGrade != null);

		this.markClean();
	}

	// --- Section updaters (used by child components) ---

	setProjectGradeResult(result: IProjectGradeResult): void {
		this.projectCriteriaGrades.set(result.criteriaGrades);

		this.projectFinalGradeID.set(result.finalGrade?.gradeID ?? null);
	}

	updateCategoryGrade(categoryID: number, gradeID: number | null): void {
		this.categoryGrades.update((current) => ({ ...current, [categoryID]: gradeID }));
	}

	updateSelectedObjectives(objectives: number[]): void {
		this.selectedObjectives.set(objectives);
	}

	updateSelectedProjectMilestone(milestoneID: number | null): void {
		this.selectedProjectMilestoneID.set(milestoneID);

		if (milestoneID != null) {
			this.completedMilestones.update((current) => {
				const next = new Set(current);
				next.add(milestoneID);
				return next;
			});
		}
	}

	updateProjectNotes(notes: string): void {
		this.projectNotes.set(notes);
	}

	updateSelectedHomeworkCategories(categoryIDs: number[]): void {
		this.selectedHomeworkCategoryIDs.set(categoryIDs);
	}

	updateHomeworkInstructions(instructions: string): void {
		this.homeworkInstructions.set(instructions);
	}

	addHomeworkLink(link: IHomeworkLink): void {
		this.homeworkLinks.update((links) => [...links, link]);
	}

	removeHomeworkLink(index: number): void {
		this.homeworkLinks.update((links) => links.filter((_, i) => i !== index));
	}

	updateSummary(summary: string): void {
		this.summary.set(summary);
	}

	updateAreasOfStrength(value: string): void {
		this.areasOfStrength.set(value);
	}

	updateAreasOfImprovement(value: string): void {
		this.areasOfImprovement.set(value);
	}

	markAttemptedSubmit(): void {
		this._attemptedSubmit.set(true);
	}

	/*
	 *	Saves final semester lesson details. Attendance and Project grades (project ONLY)
	 */
	private saveFinalSemesterLesson(lessonID: number, attendance?: IAttendanceSaveParams): void {
		const lesson = this.state.lesson();
		if (!lesson) {
			return;
		}

		const enrollmentID = lesson.enrollmentID;
		const semesterID = lesson.semesterID;

		if (!enrollmentID || !semesterID) {
			return;
		}

		this.state.isSaving.set(true);

		const projectCriteriaGrades: IProjectCriteriaGradeSaveRequest[] = Object.entries(this.projectCriteriaGrades()).map(([criteriaID, gradeID]) => ({
			enrollmentID: enrollmentID,
			semesterID: semesterID,
			gradingTemplateCategoryID: this.state.projectCategory()?.gradingTemplateCategoryID ?? 0,
			criteriaID: Number(criteriaID),
			projectGradeID: gradeID ?? 0
		}));

		const saveRequest: ISemesterFinalLessonDetailSaveRequest = {
			enrollmentID,
			lessonID,
			semesterID,
			lessonSummary: this.summary(),
			areasOfStrength: this.areasOfStrength(),
			areasOfImprovement: this.areasOfImprovement(),
			overallCalculatedProjectGradeID: this.projectFinalGradeID() ?? 0,
			projectGradingTemplateCategoryID: this.state.projectCategory()?.gradingTemplateCategoryID ?? 0,
			projectCriteriaGrades
		};

		this.lessonDetailService.saveFinalLessonDetail(saveRequest).subscribe({
			next: () => {
				this.analytics.trackEvent(AnalyticsEvent.FinalReviewSaved, {
					lessonId: lessonID,
					enrollmentId: enrollmentID,
					semesterId: semesterID,
					criteriaCount: projectCriteriaGrades.length,
					tookAttendance: attendance != null
				});

				const afterSave = () => {
					// Bust the enrollment-detail cache: the saved project/final-grade data lives in that
					// payload, and rehydrating from the stale cache makes the save appear to have not taken.
					this.state.reloadAfterChange(lessonID, true);
					this.markClean();
					this.state.isSaving.set(false);
				};

				if (attendance) {
					this.attendanceService.setAttendance(lessonID, attendance.statusID, attendance.generateInvoice).subscribe({
						next: () => {
							this.alertService.addAlert({
								title: 'Semester Completed',
								message: 'The final semester grades and attendance have been successfully saved.',
								type: 'success',
								duration: 3000
							});
							afterSave();
						},
						error: (error) => {
							console.error('Error saving attendance:', error);
							this.alertService.addAlert({
								title: 'Semester Grades Saved',
								message: 'Semester grades saved, but attendance update failed.',
								type: 'warning',
								duration: 5000
							});
							afterSave();
						}
					});
				} else {
					this.alertService.addAlert({
						title: 'Semester Completed',
						message: 'The final semester grades have been successfully saved.',
						type: 'success',
						duration: 3000
					});
					afterSave();
				}
			},
			error: (error) => {
				console.error('Error saving final semester grades:', error);
				this.alertService.addAlert({
					title: 'Save Failed',
					message: this.saveErrorMessage(error, 'An error occurred while saving the final semester grades. Please try again.'),
					type: 'error',
					duration: 5000
				});
				this.state.isSaving.set(false);
			}
		});
	}

	saveFinalSemesterWithAttendance(lessonID: number, attendance?: IAttendanceSaveParams): void {
		this.saveFinalSemesterLesson(lessonID, attendance);
	}

	/**
	 * Builds the lesson-detail payload. Shared by the complete and draft paths so the two can never
	 * drift in what they persist — the only difference between them is the isDraft flag (which
	 * suppresses the learner's "grades available" notification server-side) and whether attendance
	 * is submitted alongside.
	 */
	private buildStandardSaveRequest(lessonID: number, isDraft: boolean): ILessonDetailSaveRequest {
		const categories = this.state.gradingTemplateCategories();

		// Homework categories may be explicitly saved as "No Grade" (null), which must persist so it
		// reloads as "No Grade". All other categories only send a grade once one is actually selected.
		const homeworkCategoryIDs = new Set(
			categories.filter((c) => c.categoryType === GradingTemplateCategoryType.Homework).map((c) => c.gradingTemplateCategoryID)
		);

		return {
			lessonID: lessonID,
			grades: Object.entries(this.categoryGrades())
				.filter(([categoryID, gradeID]) => gradeID != null || homeworkCategoryIDs.has(Number(categoryID)))
				.map(([categoryID, gradeID]) => ({
					gradingTemplateCategoryID: Number(categoryID),
					gradeID: gradeID
				})),
			projectMilestones: this.newMilestones(),
			courseObjectiveIDs: this.newObjectives(),
			projectNote: this.projectNotes(),
			lessonSummary: this.summary(),
			areasOfStrength: this.areasOfStrength(),
			areasOfImprovement: this.areasOfImprovement(),
			homework:
				this.selectedHomeworkCategoryIDs().length > 0
					? {
							homeworkCategoryIDs: this.selectedHomeworkCategoryIDs(),
							notes: this.homeworkInstructions() || null,
							links: this.homeworkLinks().map((l, i) => ({
								displayText: l.name,
								homeworkLink: l.url,
								orderNumber: i + 1
							}))
						}
					: null,
			isDraft
		};
	}

	/**
	 * Saves the lesson's details without completing it: no attendance, no invoice record, no
	 * validation. This is how an instructor pre-populates a lesson they plan ahead of time. There is
	 * deliberately no attendance parameter — the absence of one is what guarantees a draft can never
	 * write attendance.
	 */
	saveDraft(lessonID: number): void {
		this.state.isSaving.set(true);

		const saveRequest = this.buildStandardSaveRequest(lessonID, true);

		this.lessonDetailService.saveLessonDetail(saveRequest).subscribe({
			next: () => {
				this.analytics.trackEvent(AnalyticsEvent.LessonDraftSaved, {
					lessonId: lessonID,
					gradeCount: saveRequest.grades.length,
					objectiveCount: saveRequest.courseObjectiveIDs.length,
					milestoneCount: saveRequest.projectMilestones.length,
					hasHomework: saveRequest.homework != null,
					homeworkCount: saveRequest.homework?.homeworkCategoryIDs.length ?? 0
				});

				this.alertService.addAlert({
					title: 'Draft Saved',
					message: 'Lesson details have been saved. Attendance is recorded when you complete the lesson.',
					type: 'success',
					duration: 3000
				});

				this.state.reloadAfterChange(lessonID, true);
				this.markClean();
				this.state.isSaving.set(false);
			},
			error: (error) => {
				console.error('Error saving lesson draft:', error);
				this.alertService.addAlert({
					title: 'Save Failed',
					message: this.saveErrorMessage(error, 'An error occurred while saving the lesson draft. Please try again.'),
					type: 'error',
					duration: 5000
				});
				this.state.isSaving.set(false);
			}
		});
	}

	saveStandardLesson(lessonID: number, attendance?: IAttendanceSaveParams): void {
		this.state.isSaving.set(true);

		const saveRequest = this.buildStandardSaveRequest(lessonID, false);

		this.lessonDetailService.saveLessonDetail(saveRequest).subscribe({
			next: () => {
				this.analytics.trackEvent(AnalyticsEvent.LessonReviewSaved, {
					lessonId: lessonID,
					gradeCount: saveRequest.grades.length,
					objectiveCount: saveRequest.courseObjectiveIDs.length,
					milestoneCount: saveRequest.projectMilestones.length,
					hasHomework: saveRequest.homework != null,
					homeworkCount: saveRequest.homework?.homeworkCategoryIDs.length ?? 0,
					tookAttendance: attendance != null
				});

				const afterSave = () => {
					this.state.reloadAfterChange(lessonID, true);
					this.markClean();
					this.state.isSaving.set(false);
				};

				if (attendance) {
					// Multi-attendance lessons submit the roster; the server derives absents and the
					// session value. Single lessons keep the classic single-value call.
					const attendanceCall = attendance.presentEnrollmentMemberIDs
						? this.attendanceService.submitRoster(lessonID, attendance.presentEnrollmentMemberIDs, attendance.generateInvoice)
						: this.attendanceService.setAttendance(lessonID, attendance.statusID, attendance.generateInvoice);

					attendanceCall.subscribe({
						next: () => {
							this.alertService.addAlert({
								title: 'Lesson Saved',
								message: 'Lesson details and attendance have been successfully saved.',
								type: 'success',
								duration: 3000
							});
							afterSave();
						},
						error: (error) => {
							console.error('Error saving attendance:', error);
							this.alertService.addAlert({
								title: 'Lesson Saved',
								message: 'Lesson details saved, but attendance update failed.',
								type: 'warning',
								duration: 5000
							});
							afterSave();
						}
					});
				} else {
					this.alertService.addAlert({
						title: 'Lesson Saved',
						message: 'Lesson details have been successfully saved.',
						type: 'success',
						duration: 3000
					});
					afterSave();
				}
			},
			error: (error) => {
				console.error('Error saving lesson details:', error);
				this.alertService.addAlert({
					title: 'Save Failed',
					message: this.saveErrorMessage(error, 'An error occurred while saving the lesson details. Please try again.'),
					type: 'error',
					duration: 5000
				});
				this.state.isSaving.set(false);
			}
		});
	}
}
